<script lang="ts">
import CommentSection from "$lib/blog/comments/CommentSection.svelte";
import { COMMENTS_EN } from "$lib/i18n/copy/comments";
import { AUTHOR_NAME, AUTHOR_PAGE, blogArticleGraph, jsonLdScript } from "$lib/seo/jsonld";
import SeoHead from "$lib/seo/SeoHead.svelte";
import SiteFooter from "$lib/seo/SiteFooter.svelte";
import { absoluteUrl } from "$lib/seo/site";
import type { PageData } from "./$types";

let { data }: { data: PageData } = $props();

const path = $derived(`/blog/${data.slug}`);
const jsonLd = $derived(
  jsonLdScript(
    blogArticleGraph({
      headline: data.title,
      description: data.description,
      url: absoluteUrl(path),
      datePublished: data.published,
      dateModified: data.modified,
      keywords: data.keywords,
    }),
  ),
);

function readable(date: string): string {
  return new Date(`${date}T00:00:00Z`).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
}
</script>

<SeoHead title={data.title} description={data.description} {path} type="article" {jsonLd} />

<div>
  <main class="mx-auto max-w-3xl px-6 py-10">
    <nav aria-label="Breadcrumb" class="text-sm text-slate-500">
      <a href="/blog" class="hover:text-slate-800">Blog</a>
    </nav>

    <article>
      <h1 class="mt-4 text-3xl font-semibold tracking-tight sm:text-4xl">{data.title}</h1>
      <p class="mt-3 text-sm text-slate-500" data-testid="byline">
        By <a href={AUTHOR_PAGE} rel="author" class="underline hover:text-slate-800">{AUTHOR_NAME}</a>
        · Published <time datetime={data.published}>{readable(data.published)}</time>
        {#if data.modified !== data.published}
          · updated <time datetime={data.modified}>{readable(data.modified)}</time>
        {/if}
      </p>

      <!-- The body is the article's own Markdown, rendered at build time from a
           file in this repository ($lib/blog/render). -->
      <div class="article-body mt-8">
        {@html data.html}
      </div>
    </article>

    <CommentSection copy={COMMENTS_EN} commentsPath={`${path}/comments`} />

    <p class="mt-12 border-t border-slate-200 pt-6 text-sm text-slate-500">
      <a href="/blog" class="underline hover:text-slate-800">All articles</a>
    </p>
  </main>

  <SiteFooter />
</div>

<style>
  .article-body {
    color: var(--color-slate-700, #334155);
    line-height: 1.75;
  }
  .article-body :global(h2) {
    margin-top: 2.5rem;
    font-size: 1.5rem;
    font-weight: 600;
    letter-spacing: -0.01em;
    color: var(--color-slate-900, #0f172a);
  }
  .article-body :global(h3) {
    margin-top: 2rem;
    font-size: 1.125rem;
    font-weight: 600;
    color: var(--color-slate-900, #0f172a);
  }
  .article-body :global(p),
  .article-body :global(ul),
  .article-body :global(ol),
  .article-body :global(table),
  .article-body :global(pre) {
    margin-top: 1rem;
  }
  .article-body :global(ul) {
    list-style: disc;
    padding-left: 1.5rem;
  }
  .article-body :global(ol) {
    list-style: decimal;
    padding-left: 1.5rem;
  }
  .article-body :global(li + li) {
    margin-top: 0.375rem;
  }
  .article-body :global(a) {
    color: var(--color-slate-900, #0f172a);
    text-decoration: underline;
  }
  .article-body :global(code) {
    font-family: var(--font-mono, ui-monospace, monospace);
    font-size: 0.875em;
  }
  .article-body :global(:not(pre) > code) {
    border-radius: 0.25rem;
    background: var(--color-slate-100, #f1f5f9);
    padding: 0.1em 0.3em;
  }
  .article-body :global(pre) {
    overflow-x: auto;
    border-radius: 0.5rem;
    background: var(--color-slate-900, #0f172a);
    padding: 1rem;
    color: var(--color-slate-100, #f1f5f9);
    font-size: 0.875rem;
    line-height: 1.6;
  }
  .article-body :global(table) {
    display: block;
    overflow-x: auto;
    border-collapse: collapse;
    font-size: 0.875rem;
  }
  .article-body :global(th),
  .article-body :global(td) {
    border-top: 1px solid var(--color-slate-200, #e2e8f0);
    padding: 0.5rem 1rem 0.5rem 0;
    text-align: left;
    vertical-align: top;
  }
  .article-body :global(th) {
    color: var(--color-slate-500, #64748b);
    font-weight: 500;
  }
  .article-body :global(blockquote) {
    margin-top: 1rem;
    border-left: 3px solid var(--color-slate-300, #cbd5e1);
    padding-left: 1rem;
    color: var(--color-slate-600, #475569);
  }
</style>
